"use client"

import * as React from "react"
import { Suspense } from "react"
import { usePathname, useRouter } from "next/navigation"
import {
  IconChartBar,
  IconUsers,
  IconLayoutGrid,
  IconChecklist,
  IconAlertTriangle,
  IconClockHour4,
  IconMessageCircle,
  IconTargetArrow,
  IconCertificate,
  IconTool,
} from "@tabler/icons-react"
import { MessageCircleMore, Bell } from "lucide-react"
import { useNotifications } from "@/lib/notifications-context"

import { NavMain } from "@/components/nav-main"
import { NavUser } from "@/components/nav-user"
import { NavFeedback } from "@/components/feedback/feedback-button"
import { RecentChatsSidebar } from "@/components/recent-chats-sidebar"
import { Separator } from "@/components/ui/separator"
import { useUser } from "@/components/user-context"
import { canViewAnalytics, canManageUsers } from "@/lib/permissions"
import { hasElevatedViewAccess } from "@/lib/workspace-permissions"
import { editSessionStore } from "@/lib/edit-session-store"
import { completeScenarioEditSession } from "@/lib/api-v3"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"

const data = {
  navMain: [
    {
      title: "Задачи",
      url: "/procedures/assigned",
      icon: IconChecklist,
    },
    {
      title: "Библиотека",
      url: "/files",
      icon: IconLayoutGrid,
    },
    {
      title: "Исследование времени",
      url: "/time-studies",
      icon: IconClockHour4,
    },
    {
      title: "Матрица компетенций",
      url: "/competency",
      icon: IconTargetArrow,
    },
    {
      title: "Тестирование",
      url: "/testing",
      icon: IconCertificate,
    },
    {
      title: "Инциденты",
      url: "/incidents",
      icon: IconAlertTriangle,
    },
    {
      title: "ТОиР",
      url: "/maintenance",
      icon: IconTool,
      items: [
        { title: "Обзор",        url: "/maintenance" },
        { title: "Оборудование", url: "/maintenance/equipment" },
        { title: "Планирование", url: "/maintenance/planning" },
        { title: "Задания",      url: "/maintenance/work-orders" },
        { title: "Дефекты",      url: "/maintenance/defects" },
        { title: "Материалы",    url: "/maintenance/materials" },
        { title: "Отчёты",       url: "/maintenance/reports" },
        { title: "Настройки",    url: "/maintenance/settings" },
      ],
    },
    {
      title: "Copilot",
      url: "/copilot-v15",
      icon: MessageCircleMore,
    },
    {
      title: "Мессенджер",
      url: "/messenger",
      icon: IconMessageCircle,
    },
    {
      title: "Пользователи",
      url: "/users",
      icon: IconUsers,
    },
    {
      title: "Аналитика",
      url: "/analytics",
      icon: IconChartBar,
      items: [
        { title: "Обзор",                url: "/analytics" },
        { title: "Отчёты по процедурам",  url: "/analytics/scenarios" },
        { title: "Отчёты по чек-листам", url: "/analytics/forms" },
        { title: "Запросы в Copilot",    url: "/analytics/copilot" },
        { title: "Аналитика по инцидентам", url: "/analytics/incidents" },
      ],
    },
  ],
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const { user, userGroup, userWorkspace, isWorkspaceLoaded } = useUser()
  const predefinedRole = userWorkspace?.role?.predefinedRole
  const hasElevatedView = hasElevatedViewAccess(predefinedRole)
  const { hasUnread } = useNotifications()
  const { isMobile } = useSidebar()
  const pathname = usePathname()
  const router = useRouter()
  const isCopilotV15 = pathname === "/copilot-v15" || pathname.startsWith("/copilot-v15/")

  const handleLogoClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    const guard = (window as any).__navGuard as ((url: string) => boolean) | undefined
    if (guard && !guard(logoHref)) return
    const session = editSessionStore.get()
    if (session) {
      editSessionStore.clear()
      try {
        await completeScenarioEditSession(session.accessToken, session.scenarioId)
      } catch {
        // Продолжаем навигацию даже при ошибке
      }
    }
    router.push(logoHref)
  }

  // Формируем данные пользователя для NavUser
  const userData = user ? {
    name: user.name,
    email: user.login, // Используем login как есть
    avatar: "/avatars/shadcn.jpg", // Пока используем дефолтный аватар
  } : {
    name: "Пользователь",
    email: "Пользователь",
    avatar: "/avatars/shadcn.jpg",
  }

  // Фильтруем пункты меню в зависимости от роли пользователя
  const filteredNavItems = data.navMain.filter(item => {
    if (item.url === "/users" && !canManageUsers(userGroup) && !hasElevatedView) return false
    if (item.url === "/analytics" && !canViewAnalytics(userGroup) && !hasElevatedView) return false
    return true
  })

  // Определяем ссылку для логотипа в зависимости от прав
  const logoHref = (canViewAnalytics(userGroup) || hasElevatedView) ? "/analytics" : "/procedures/assigned"

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              className={
                isMobile 
                  ? "data-[slot=sidebar-menu-button]:!p-2" // Умеренные отступы для мобильных
                  : "data-[slot=sidebar-menu-button]:!p-1.5"
              }
            >
              <a href={logoHref} onClick={handleLogoClick} className="flex items-center gap-2 font-medium">
                <img 
                  src="/logo_mini.png" 
                  alt="Casperai Logo" 
                  className={isMobile ? "size-7" : "size-6"} // Немного больший логотип для мобильных
                />
                <span className={
                  isMobile 
                    ? "text-lg font-semibold" // Умеренно больший заголовок для мобильных
                    : "text-base font-semibold"
                }>
                  Casperai
                </span>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      
      <SidebarContent className={isCopilotV15 ? "overflow-hidden" : ""}>
        {!isWorkspaceLoaded ? (
          <div className="flex flex-col gap-2 px-2 py-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-9 rounded-lg bg-muted animate-pulse" />
            ))}
          </div>
        ) : (
          <NavMain
            items={filteredNavItems}
            notificationsHasUnread={hasUnread}
          />
        )}
        {isCopilotV15 && isWorkspaceLoaded && (
          <Suspense fallback={null}>
            <RecentChatsSidebar />
          </Suspense>
        )}
      </SidebarContent>
      
      <SidebarFooter className={isCopilotV15 ? "pt-0" : ""}>
        {isCopilotV15 && <Separator className="mb-2" />}
        <NavFeedback />
        <NavUser user={userData} />
      </SidebarFooter>
    </Sidebar>
  )
}
